/**
 * One pivot point candidate, with its evidence (specs/pivot-points-v2-prd.md §5).
 * Every count is a link that opens the map on those nodes. The direction is a
 * facet, not a score: it changes what the room is asked to do.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Check, X, RotateCcw } from 'lucide-react';

export const DIRECTION = {
  attractor:   { label: 'NOIDANKEHÄ',    color: '#e0708a', bg: 'rgba(224,112,138,0.14)' },
  opportunity: { label: 'MAHDOLLISUUS',  color: '#3ecf8e', bg: 'rgba(62,207,142,0.14)' },
  contested:   { label: 'KIISTANALAINEN', color: '#c9cbd6', bg: 'rgba(201,203,214,0.12)' },
};

/* Whose reading the direction is. The AI's valence counts (17.9.2026); the
   card says so, and the room can overrule it on the map. */
export const SOURCE = {
  human: 'huoneen arvio',
  ai: 'tekoälyn lukema',
  mixed: 'huone ja tekoäly',
  none: 'ei arvioita',
};

/* The two lanes (§10). Yhteinen needs no badge — it is what a pivot has
   always been. Rakenteellinen says why this is here without convergence. */
export const LANE = {
  rakenteellinen: { label: 'RAKENTEELLINEN', color: '#f0b35e', bg: 'rgba(240,179,94,0.14)', hint: 'Vipuvoima ilman yhteneväisyyttä: yksi kirjoittaja, mutta kierteellä, valintakohdassa tai esteen takana.' },
};
export const directionSource = (c) => c?.leverage?.direction?.source || c?.directionFacts?.source || 'none';

const Card = styled.article`
  background: #1a1a26; border: 1px solid #2f2f40; border-radius: 14px; padding: 16px 18px;
  color: #e8e8ef; display: flex; flex-direction: column; gap: 8px; cursor: pointer;
  opacity: ${(p) => (p.$status === 'dismissed' ? 0.45 : 1)};
  border-color: ${(p) => (p.$selected ? '#a78bfa' : p.$status === 'accepted' ? '#7c3aed' : '#2f2f40')};
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px rgba(167,139,250,0.25)' : 'none')};
  &:hover { border-color: ${(p) => (p.$selected ? '#a78bfa' : '#4a4a66')}; }
  &:focus-visible { outline: 1px solid #a78bfa; }
`;
const Head = styled.div`display: flex; gap: 12px; align-items: baseline;`;
const Rank = styled.span`font-family: ui-monospace, monospace; font-size: 0.72rem; color: #8a8fa3; min-width: 1.6em;`;
const Title = styled.h3`margin: 0; font-size: 1.05rem; font-weight: 600; flex: 1; line-height: 1.3;`;
const Dir = styled.span`
  font-family: ui-monospace, monospace; font-size: 0.66rem; letter-spacing: 0.1em; padding: 2px 8px; border-radius: 5px;
  color: ${(p) => p.$c}; background: ${(p) => p.$bg}; white-space: nowrap;
`;
const Tags = styled.span`display: inline-flex; gap: 6px; align-items: center; flex-wrap: wrap; justify-content: flex-end;`;
const Src = styled.span`font-size: 0.7rem; color: #8a8fa3; white-space: nowrap;`;
const Evidence = styled.div`display: flex; flex-direction: column; gap: 4px; color: #c9cbd6; font-size: 0.86rem;`;
const Chip = styled.a`
  color: #c4b5fd; text-decoration: none; border-bottom: 1px dotted #7c3aed;
  &:hover { color: #e9d5ff; border-bottom-style: solid; }
`;
const Desc = styled.p`margin: 4px 0 0; color: #b4b8c5; font-size: 0.9rem; line-height: 1.5;`;
const Actions = styled.div`display: flex; gap: 6px; margin-top: 6px;`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.04); color: #ddd; font: inherit; font-size: 0.82rem; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.09); }
  &.accept { border-color: rgba(167,139,250,0.6); color: #e9d5ff; }
`;

export default function PivotCard({ c, mapHref, onStatus, selected = false, onSelect, cardRef }) {
  const d = DIRECTION[c.direction] || DIRECTION.contested;
  const src = directionSource(c);
  const lane = LANE[c.lane];
  const conv = c.convergence || {};
  const lev = c.leverage || {};
  const f = lev.facts || {};
  const members = c.memberIds || [];
  const link = (ids, text) => <Chip href={mapHref(ids)} title="Näytä kartalla">{text}</Chip>;
  const loopBits = [];
  if (f.reinforcingLoops) loopBits.push(`${f.reinforcingLoops} vahvistava${f.reinforcingLoops > 1 ? 'a' : ''} kierre${f.reinforcingLoops > 1 ? 'ttä' : ''}`);
  if (f.balancingLoops) loopBits.push(`${f.balancingLoops} tasapainottava${f.balancingLoops > 1 ? 'a' : ''} kierre${f.balancingLoops > 1 ? 'ttä' : ''}`);
  if (f.choicePoint) loopBits.push('valintakohta');
  if (f.crossLinks) loopBits.push(`${f.crossLinks} ristiinyhteyttä`);
  if (f.blockers) loopBits.push(`${f.blockers} estettä`);
  if (f.descendants) loopBits.push(`${f.descendants} seurausta alla`);
  // The whole card opens the pivot; chips and buttons keep their own jobs.
  const stop = (e) => e.stopPropagation();
  return (
    <Card $status={c.status} $selected={selected} ref={cardRef} tabIndex={0} role="button" aria-pressed={selected}
      onClick={() => onSelect?.(c)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(c); } }}>
      <Head>
        <Rank>{c.rank}</Rank>
        <Title>{c.title}</Title>
        <Tags>
          {lane && <Dir $c={lane.color} $bg={lane.bg} title={lane.hint}>{lane.label}</Dir>}
          <Dir $c={d.color} $bg={d.bg}>{d.label}</Dir>
          <Src title="Kenen arvio suunta on">{SOURCE[src]}</Src>
        </Tags>
      </Head>
      <Evidence onClick={stop}>
        <span>
          {link(members, `${conv.scenarios} skenaariota · ${conv.participants} osallistujaa`)}
          {conv.confirmedSame ? ` · ${conv.confirmedSame} sama asia -yhteyttä` : ''}
          {conv.votesUp ? ` · +${conv.votesUp} ääntä` : ''}
        </span>
        <span>{loopBits.length ? link(lev.bestMember ? [lev.bestMember] : members, loopBits.join(' · ')) : 'Ei rakenteellista vipuvoimaa merkitty — konvergenssi vain.'}</span>
      </Evidence>
      {c.description && <Desc>{c.description}</Desc>}
      {onStatus && (
        <Actions onClick={stop}>
          {c.status !== 'accepted' && <Btn className="accept" type="button" onClick={() => onStatus(c, 'accepted')}><Check size={14} /> Hyväksy</Btn>}
          {c.status !== 'dismissed' && <Btn type="button" onClick={() => onStatus(c, 'dismissed')}><X size={14} /> Hylkää</Btn>}
          {c.status !== 'proposed' && <Btn type="button" onClick={() => onStatus(c, 'proposed')}><RotateCcw size={14} /> Palauta</Btn>}
        </Actions>
      )}
    </Card>
  );
}
